/**
 * Presence status: the pure half of the avatar status badge.
 *
 * No React, no stores, no side effects — everything here is a function of its
 * arguments so it can be tested without a renderer and reused by any call site.
 *
 * Three deliberate design points:
 *
 * 1. **`away` is never inferred.** The app's only activity signal
 *    (`services/vaultActivity.js`) is DOM-level and blind to other
 *    applications, and the OS suspend/screen-lock hooks in
 *    `src-tauri/src/vault_lock_platform.rs` are Windows-only. An in-app idle
 *    timer would mark a macOS user "away" while they type in another window.
 *    So `away` and `busy` are user-set, per device, and no timer ships.
 *
 * 2. **The value space has FIVE states, not four:**
 *    `online | away | busy | offline | null`.
 *    `null` means UNKNOWN — we have not been told anything about this user yet
 *    — and must render NOTHING. `offline` means we have been told, by the
 *    server, that nobody is connected. Between app launch and the first
 *    `presence:sync` every foreign user is `null`; painting them all offline
 *    for that second is a visible flash and, worse, a confident lie. The two
 *    states are never merged. `resolveStatus` is where they are told apart.
 *
 * 3. **Two status LISTS, and they are not the same list.**
 *    `SETTABLE_STATUSES` is what a human may pick and what this device may push
 *    to the server; `STATUSES` is what may be RENDERED. `offline` is renderable
 *    but not settable (nobody chooses to be offline — `invisible` is that
 *    choice, and the server rejects a pushed `"offline"` with
 *    `invalid_status`), and `invisible` is settable but never rendered for
 *    anybody but you.
 *
 * Foreign statuses arrive from `store/userPresenceStore.js`, fed by
 * `services/userPresenceService.js` — the only module in `src/` that speaks the
 * presence protocol. Nothing in THIS module pushes anything to a channel.
 */

/**
 * Every status that may be RENDERED as a badge, including `offline`.
 * `PresenceBadge` gates on this list, so a status missing here renders nothing.
 */
export const STATUSES = ['online', 'away', 'busy', 'invisible', 'offline'];

/**
 * Every status a human may PICK and this device may push to the server.
 *
 * Deliberately not `STATUSES`: `StatusMenu` maps this list, so adding `offline`
 * to `STATUSES` (which the always-visible dots need) must not silently grow an
 * "Offline" row in the account menu — the server answers `invalid_status` to a
 * pushed `"offline"`, and "appear offline" is spelled `invisible` here.
 */
export const SETTABLE_STATUSES = ['online', 'away', 'busy', 'invisible'];

/**
 * What OTHER people are allowed to see. `invisible` means "appear offline", so
 * it must never be rendered on anybody else's screen — an invisible user has to
 * look exactly like someone who is not connected.
 *
 * `offline` IS on this list (that is the always-visible-dots feature) and
 * `invisible` is NOT (that is the privacy promise). Getting these two the wrong
 * way round is the difference between the feature and a lie, so the list is
 * stated once, here, and asserted by `presenceStatusResolve.test.js`.
 */
export const STATUSES_VISIBLE_TO_OTHERS = ['online', 'away', 'busy', 'offline'];

/** A status this device may set/store, or `null`. */
function validSettableStatus(value) {
  return SETTABLE_STATUSES.includes(value) ? value : null;
}

/** As above, but for a status arriving from somebody else. */
function validForeignStatus(value) {
  return STATUSES_VISIBLE_TO_OTHERS.includes(value) ? value : null;
}

/**
 * The badge to show for `userId`, or `null` to render nothing.
 *
 * @param {object}  args
 * @param {*}       args.userId       whose avatar this is
 * @param {*}       args.selfId       the signed-in user's id
 * @param {string}  args.selfStatus   this device's user-set status
 * @param {object}  args.presenceMeta `{ status }` for `userId` (may be null)
 * @param {boolean} args.isPresent    do we KNOW anything about `userId`
 * @returns {'online'|'away'|'busy'|'invisible'|'offline'|null}
 *   `'invisible'` is only ever returned for YOUR OWN avatar — it is how you see
 *   that you are hidden. For anyone else it collapses to `'offline'`.
 */
export function resolveStatus({ userId, selfId, selfStatus, presenceMeta, isPresent } = {}) {
  if (userId == null) return null;

  // Self is keyed on identity, not on "a status exists": your own badge shows
  // your own device state even with an empty store (free plan, socket down).
  if (selfId != null && String(userId) === String(selfId)) {
    return validSettableStatus(selfStatus) || 'online';
  }

  // UNKNOWN, not offline. `isPresent === false` here means "the store has told
  // us nothing about this user" — before the first presence:sync, on the free
  // (local-only) plan, or with the socket down. Rendering a grey dot for that
  // is a statement we cannot support; render nothing.
  if (!isPresent) return null;

  // An invisible user must be indistinguishable from an offline one. The server
  // already collapses this (Stage 1: `invisible` cannot reach a watcher's topic
  // at all), so this is defence in depth against an old server or a replayed
  // frame — and it must map to 'offline', NOT to null, or a hiding user would
  // be the one user in the sidebar with no dot and hiding would be detectable.
  if (presenceMeta && presenceMeta.status === 'invisible') return 'offline';

  // No `|| 'online'` here, and never again. When the server says nothing useful
  // about a user we know it is tracking, the honest answer is `offline` — the
  // old `|| 'online'` fallback would paint EVERY offline user online the moment
  // the server started sending statuses, i.e. exactly the inverse of this
  // feature. `presenceStatusResolve.test.js` fails if it comes back.
  return validForeignStatus(presenceMeta && presenceMeta.status) || 'offline';
}

function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

/**
 * Badge diameter and ring width for an avatar of `avatarSize` px, or `null`
 * when the avatar is too small to carry a legible badge.
 *
 * The geometry table this produces (PLAN.md):
 *   20 -> 9/1.5 · 26 -> 9/1.5 · 28 -> 10/1.5 · 32 -> 12/2 · 34 -> 12/2 · 56 -> 16/2
 * The ring steps off the CLAMPED diameter, not off the raw ratio — at 28px the
 * raw ratio is 10.08 and would take the thick ring while the badge is 10px.
 */
export function badgeGeometry(avatarSize) {
  const size = Number(avatarSize);
  if (!Number.isFinite(size) || size < 18) return null;
  const d = clamp(Math.round(size * 0.36), 9, 16);
  return { d, ring: d <= 10 ? 1.5 : 2 };
}

/*
 * The roster readers (`tokenFromRawState`, `tokenFromMemberLists`,
 * `tokenFromTeams`, `firstToken`, `PRESENT_NO_STATUS`) lived here until
 * 27.08.2026 and are deliberately GONE, not merely unused.
 *
 * They derived a foreign user's presence by first-match across the three
 * rosters the app happens to hold (`chatStore.presenceByChannel`,
 * `presenceStore.rawState`, `teamPresenceStore.byTeam`). Those rosters cover a
 * SUBSET of the people you can see — `chatChannelService` joins exactly one
 * `chat:{id}` topic at a time, and a DM partner need share neither a workspace
 * nor a team — so "not in any roster" never meant "offline", it meant "we did
 * not look". That was harmless while the only rendered state was `online`; with
 * an always-visible dot it would paint an online user offline on essentially
 * every sidebar row (the architect's option A, rejected in the 27.08.2026
 * plan, §Q1).
 *
 * Foreign presence now has exactly one source: `store/userPresenceStore.js`,
 * fed by the server's `presence:sync` / `presence:update` over one watch set.
 * Leaving the roster readers exported would be an open invitation to re-derive
 * presence from a roster again, which is why they are deleted rather than kept
 * "in case". `presenceStore` and `teamPresenceStore` themselves stay — they
 * still back `selectRequestViewers` and the workspace roster UI.
 */
